import { SkyBadge, SkyButton, SkyPopover, SkyText } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import { IMPORT_CAUSE_ACTION_META, type ImportCauseView } from "@shared/types/importCauses";
import { roleHasPermission } from "@shared/permissions";
import {
  WHO_ACTS_SKYPOINT_RETRY_HINT,
  WHO_ACTS_SKYPOINT_RUN_RETRY_HINT,
  nextStepFor,
  whoActs,
} from "@shared/types/whoActs";

/**
 * A failed import's classified cause and its next step (product gaps row 4, design §4.7).
 *
 * The label, the sentence and the fix are the SERVER's (`server/services/importCauseView.ts`);
 * this component never composes a sentence. The label is a badge (colour AND words, never colour
 * alone), then what happened, then "Next step: {fix}", then one button for the cause's action.
 *
 * The button is drawn only when the page can take the action (`action`) and the reader holds its
 * permission (`permitted`). A reader without it reads the fix alone, or, when the fix is in
 * Destination, "Ask Skypoint support to make this change in Destination." (#1659), or the cause's own line.
 *
 * The "Next step" follows who acts on the cause (#829, R6.5, `whoActs`): a cause on Skypoint's
 * side reads Skypoint's own words in place of the fix; every other cause keeps the server's fix
 * (`unknown` too, owner direction on #839, until #815 moves the text codes). A Skypoint cause also drops the "ask your
 * tenant admin" line: no one is asked to change anything. The words are `shared/types/whoActs.ts`
 * constants, the ones the notification renderer prints too.
 *
 * A firewall cause lists the platform's outbound addresses the web read from Azure (W3-1), each
 * in monospace so it can be copied as written.
 */

/**
 * The fixed copy of the "Why no error text?" popover (design §4.7). #1374 item 3: the block offers
 * it only for the `unknown` cause, the one with no label or sentence of its own to explain.
 */
export const WHY_NO_IMPORT_ERROR_TEXT =
  "skyData keeps a code and the names involved. The source's and the warehouse's own text can quote values from your data, so it stays in the job's logs.";

export interface ImportCauseAction {
  label: string;
  onClick: () => void;
}

/** What a page supplies for its cause blocks: where each action goes, and who may take it. */
/** Where a cause's block sits, so the page does not draw a button the place already has. */
export interface ImportCauseContext {
  /** True in a step's details that draw the step's own "Retry the {step} step" button. */
  stepRetryShown?: boolean;
}

export interface ImportCauseHandlers {
  action: (
    cause: ImportCauseView,
    dataflowId: string | null,
    context?: ImportCauseContext,
  ) => ImportCauseAction | null;
  permitted: (cause: ImportCauseView) => boolean;
  /** The line a reader without the key reads; absent, the cause's own `withoutPermission`. */
  askLine?: (cause: ImportCauseView) => string | null;
}

export interface ImportCauseBlockProps {
  cause: ImportCauseView;
  /** The action the page can take for this cause; null or absent draws no button. */
  action?: ImportCauseAction | null;
  /** True when the reader holds `cause.permission` (or it needs none). */
  permitted: boolean;
  /**
   * What a reader without the key reads instead of the button; absent, the cause's own
   * `withoutPermission`.
   */
  askLine?: string | null;
  /** A table cell's popover: no table count, a smaller sentence. */
  compact?: boolean;
  /**
   * #1084: the button is the page's primary action here (`fixBeforeRetry`): a retry beside it
   * cannot help until this cause is fixed, so the retries draw as secondary buttons after it.
   */
  actionPrimary?: boolean;
  testId?: string;
}

/** True when a reader with `canAny` may take the cause's action. */
export function causePermitted(
  cause: Pick<ImportCauseView, "permission">,
  canAny: (key: NonNullable<ImportCauseView["permission"]>) => boolean,
): boolean {
  return cause.permission === null || canAny(cause.permission);
}

function nameWhyDialog(element: HTMLElement | null): void {
  element?.setAttribute("aria-label", "Why no error text?");
}

/** The Next step line: by who acts, else the server's fix. */
export function nextStepOf(cause: Pick<ImportCauseView, "source" | "code" | "fix">): string {
  return nextStepFor(cause.source, cause.code, cause.fix) ?? cause.fix;
}

/**
 * #829 review M3: a Skypoint cause still shows its fix to the reader who takes it, Skypoint's own
 * staff: one holding the cause's action key where a tenant admin does not (the key is
 * PlatformAdmin's alone, `instance.binding.write`). Everyone else reads only Skypoint's words.
 */
export function showsFixToSkypoint(
  cause: Pick<ImportCauseView, "source" | "code" | "permission">,
  permitted: boolean,
): boolean {
  return (
    permitted &&
    cause.permission !== null &&
    whoActs(cause.source, cause.code) === "skypoint" &&
    !roleHasPermission("TenantAdmin", cause.permission)
  );
}

function tablesWord(count: number): string {
  return `${count.toLocaleString("en-US")} ${count === 1 ? "table" : "tables"}`;
}

type TextSize = 200 | 300;

/** The cause's label badge and, outside a compact cell, its table count. */
function CauseBadges({
  cause,
  compact,
  testId,
}: {
  cause: ImportCauseView;
  compact: boolean;
  testId: string;
}) {
  const showTables = !compact && cause.tables !== undefined && cause.tables > 0;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <SkyBadge appearance="tint" color="danger" testId={`${testId}-label`}>
        {cause.label}
      </SkyBadge>
      {showTables ? (
        <SkyBadge appearance="outline" color="subtle" testId={`${testId}-tables`}>
          {tablesWord(cause.tables ?? 0)}
        </SkyBadge>
      ) : null}
    </div>
  );
}

/** The outbound addresses (W3-1), beside the fix the reader reads; none without the fix. */
function CauseAddresses({
  cause,
  permitted,
  testId,
}: {
  cause: ImportCauseView;
  permitted: boolean;
  testId: string;
}) {
  const addresses = cause.addresses ?? [];
  const fixShown = nextStepOf(cause) === cause.fix || showsFixToSkypoint(cause, permitted);
  if (addresses.length === 0 || !fixShown) return null;
  return (
    <ul
      className="flex flex-wrap gap-1"
      aria-label="skyData's outbound addresses"
      data-testid={`${testId}-addresses`}
    >
      {addresses.map((address) => (
        <li key={address}>
          <SkyBadge appearance="outline" color="subtle">
            <span className="font-mono">{address}</span>
          </SkyBadge>
        </li>
      ))}
    </ul>
  );
}

/** The cause's one button, and on a Skypoint cause that keeps Retry, the line saying why (#839). */
function CauseAction({
  cause,
  action,
  skypointActs,
  size,
  primary,
  testId,
}: {
  cause: ImportCauseView;
  action: ImportCauseAction;
  skypointActs: boolean;
  size: TextSize;
  primary: boolean;
  testId: string;
}) {
  return (
    <>
      {skypointActs && cause.action === "retry_failed_tables" ? (
        // #839: the button stays beside "You do not need to change anything", so say why.
        <SkyText size={size} block testId={`${testId}-retry-hint`}>
          {cause.actionLabel === IMPORT_CAUSE_ACTION_META.retry_failed_tables.label
            ? WHO_ACTS_SKYPOINT_RETRY_HINT
            : WHO_ACTS_SKYPOINT_RUN_RETRY_HINT}
        </SkyText>
      ) : null}
      <div>
        <SkyButton
          appearance={primary ? "primary" : "secondary"}
          size="small"
          onClick={action.onClick}
          testId={`${testId}-action`}
        >
          {action.label}
        </SkyButton>
      </div>
    </>
  );
}

export function ImportCauseBlock({
  cause,
  action = null,
  permitted,
  askLine,
  compact = false,
  actionPrimary = false,
  testId = "import-cause",
}: ImportCauseBlockProps) {
  const [whyOpen, setWhyOpen] = useState(false);
  const shownAction = permitted ? action : null;
  const ask = askLine === undefined ? cause.withoutPermission : askLine;
  // #829: a cause on Skypoint's side asks no one to change anything, so no "ask your admin" line.
  const skypointActs = whoActs(cause.source, cause.code) === "skypoint";
  const askAdmin = !permitted && ask && !skypointActs ? ask : null;
  const size: TextSize = compact ? 200 : 300;
  return (
    <div
      className="flex min-w-0 flex-col gap-2"
      data-testid={testId}
      data-cause-code={cause.code}
      data-cause-source={cause.source}
    >
      <CauseBadges cause={cause} compact={compact} testId={testId} />
      <SkyText size={size} block testId={`${testId}-sentence`}>
        {`${cause.sentence}.`}
      </SkyText>
      <SkyText size={size} block testId={`${testId}-fix`}>
        <span className="font-semibold">Next step:</span> {nextStepOf(cause)}
      </SkyText>
      {showsFixToSkypoint(cause, permitted) ? (
        <SkyText size={size} block testId={`${testId}-skypoint-fix`}>
          <span className="font-semibold">For Skypoint:</span> {cause.fix}
        </SkyText>
      ) : null}
      <CauseAddresses cause={cause} permitted={permitted} testId={testId} />
      {shownAction ? (
        <CauseAction
          cause={cause}
          action={shownAction}
          skypointActs={skypointActs}
          size={size}
          primary={actionPrimary}
          testId={testId}
        />
      ) : null}
      {askAdmin ? (
        <SkyText size={200} color="muted" block testId={`${testId}-ask-admin`}>
          {askAdmin}
        </SkyText>
      ) : null}
      {/* #1374 item 3: the link answers a question only a cause with no detail raises. A drawn
          cause already has its label, sentence and next step, so only `unknown` offers it. */}
      {cause.code === "unknown" ? (
        <SkyPopover
          // SkyPopover renders a role=dialog and forwards no aria props, so the dialog is named
          // through its ref, as `CauseBlock` does (NFR-7).
          ref={nameWhyDialog}
          open={whyOpen}
          onOpenChange={setWhyOpen}
          placement="bottom-start"
          className="max-w-xs p-3"
          testId={`${testId}-why`}
          trigger={
            <button
              type="button"
              className="self-start text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-expanded={whyOpen}
              onClick={() => setWhyOpen((open) => !open)}
              data-testid={`${testId}-why-button`}
            >
              Why no error text?
            </button>
          }
        >
          <SkyText size={200}>{WHY_NO_IMPORT_ERROR_TEXT}</SkyText>
        </SkyPopover>
      ) : null}
    </div>
  );
}
